
import { ContractContent, ViewerRole } from '../../types/contracts';

interface ContractViewerProps {
  content: ContractContent;
  viewerRole: ViewerRole; // 'agent' sees all; 'buyer' sees all + buyer; 'seller' sees all + seller
  className?: string;
}

export function ContractViewer({ content, viewerRole, className = '' }: ContractViewerProps) {
  // Filter fields based on role-based visibility
  const visibleFields = content.fields.filter(f => {
    if (viewerRole === 'agent') return true;
    if (f.visibility === 'agent_only') return false;
    if (f.visibility === 'all') return true;
    if (f.visibility === 'buyer' && viewerRole === 'buyer') return true;
    if (f.visibility === 'seller' && viewerRole === 'seller') return true;
    return false;
  });

  // Replace merge fields in terms with actual field values
  const renderTerms = () => {
    let htmlContent = content.terms
      // Escape HTML special chars to prevent XSS
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Preserve newlines
      .replace(/\n/g, '<br/>');

    // Inject visible field values
    visibleFields.forEach(field => {
      const regex = new RegExp(`\\{\\{${field.id}\\}\\}`, 'g');
      const displayValue = field.value
        ? `<span class="font-bold" style="color:var(--t-primary)">${field.value}</span>`
        : `<span style="color:var(--t-error)">[Missing: ${field.name}]</span>`;
      htmlContent = htmlContent.replace(regex, displayValue);
    });

    // Replace hidden field placeholders
    const hiddenFields = content.fields.filter(f => !visibleFields.includes(f));
    hiddenFields.forEach(field => {
      const regex = new RegExp(`\\{\\{${field.id}\\}\\}`, 'g');
      htmlContent = htmlContent.replace(regex, `<span class="italic" style="color:var(--t-text-muted);background:var(--t-surface-hover);padding:0 4px;border-radius:4px">[Hidden]</span>`);
    });

    return { __html: htmlContent };
  };

  return (
    <div className={`bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl p-8 max-w-4xl mx-auto shadow-sm ${className}`}>
      {visibleFields.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-8 pb-8 border-b border-[var(--t-border)]">
          {visibleFields.map(field => (
            <div key={field.id} className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider">
                {field.name}
              </span>
              <div className="text-[var(--t-text)] font-medium p-3 bg-[var(--t-bg)] rounded-xl border border-[var(--t-border-subtle)]">
                {field.value || <span className="text-[var(--t-text-muted)] italic">Not specified</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[var(--t-text)] leading-relaxed text-sm" dangerouslySetInnerHTML={renderTerms()} />
    </div>
  );
}
